/**
 * Tab yönetimi hook'u
 * Multi-tab durumunda tab seçimi, field yükleme ve senkronizasyon
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import type { SearchField } from '@/types/product-search'
import type { ProductType } from '@/lib/api/product-types'

export interface UseTabManagementReturn {
    selectedTabIndex: number
    tabSearchFields: Record<string, SearchField[]>
    tabLoadingStates: Record<string, boolean>
    activeSearchFields: SearchField[]
    activeTabLoading: boolean
    activeProductTypeForTab: string
    setSelectedTabIndex: (index: number) => void
    handleTabChange: (index: number) => void
}

export function useTabManagement(
    productTypes: ProductType[],
    searchFields: SearchField[],
    resolvedActiveProductType: string,
    urlProductType: string | null,
    preferencesProductType: string | undefined,
    isLoading: boolean,
    resolvedLanguage: string,
    multiTab: boolean,
    setProductType: (type: 'tour' | 'ship') => void
): UseTabManagementReturn {
    const [selectedTabIndex, setSelectedTabIndex] = useState(0)
    const [tabSearchFields, setTabSearchFields] = useState<Record<string, SearchField[]>>({})
    const [tabLoadingStates, setTabLoadingStates] = useState<Record<string, boolean>>({})
    const loadedTabsRef = useRef<Set<string>>(new Set())
    
    const resolvedMultiTab = useMemo(() => multiTab && productTypes.length > 0, [multiTab, productTypes.length])
    
    // Tab index'i URL ve cookie'den senkronize et (birleştirilmiş useEffect)
    useEffect(() => {
        if (!resolvedMultiTab || productTypes.length === 0) return
        
        const targetProductType = resolvedActiveProductType || preferencesProductType || productTypes[0]?.type_key
        if (!targetProductType) return
        
        const targetIndex = productTypes.findIndex(pt => pt.type_key === targetProductType)
        if (targetIndex >= 0 && targetIndex !== selectedTabIndex) {
            setSelectedTabIndex(targetIndex)
            
            // URL'den gelmediyse ve cookie ile uyumsuzsa cookie'yi güncelle
            if (!urlProductType && preferencesProductType && preferencesProductType !== targetProductType) {
                setProductType(targetProductType as 'tour' | 'ship')
            }
        }
    }, [resolvedMultiTab, productTypes, resolvedActiveProductType, urlProductType, preferencesProductType, selectedTabIndex, setProductType])
    
    // Tab değiştiğinde cookie'yi güncelle
    useEffect(() => {
        if (!resolvedMultiTab || productTypes.length === 0) return
        
        const newProductType = productTypes[selectedTabIndex]?.type_key
        if (newProductType && newProductType !== preferencesProductType && newProductType !== resolvedActiveProductType) {
            if (!resolvedActiveProductType || resolvedActiveProductType === productTypes[0]?.type_key) {
                setProductType(newProductType as 'tour' | 'ship')
            }
        }
    }, [selectedTabIndex, resolvedMultiTab, productTypes, preferencesProductType, resolvedActiveProductType, setProductType])
    
    // Tab fields yükleme - optimize edilmiş (race condition önlendi)
    useEffect(() => {
        if (!resolvedMultiTab || productTypes.length === 0) return
        
        const tabsToLoad = productTypes.filter(pt => {
            const loadKey = `${pt.type_key}-${resolvedLanguage}`
            return !loadedTabsRef.current.has(loadKey) && !tabLoadingStates[pt.type_key] && !tabSearchFields[pt.type_key]
        })
        
        if (tabsToLoad.length === 0) return
        
        const loadPromises = tabsToLoad.map(async (pt) => {
            const typeKey = pt.type_key
            const loadKey = `${typeKey}-${resolvedLanguage}`
            
            if (tabLoadingStates[typeKey] || tabSearchFields[typeKey] || loadedTabsRef.current.has(loadKey)) {
                return
            }
            
            setTabLoadingStates(prev => ({ ...prev, [typeKey]: true }))
            try {
                const { getProductFilters } = await import('@/lib/api/product-search')
                const filters = await getProductFilters(typeKey, resolvedLanguage)
                
                setTabSearchFields(prev => {
                    if (prev[typeKey]) return prev
                    return { ...prev, [typeKey]: filters.search_fields || [] }
                })
                loadedTabsRef.current.add(loadKey)
            } catch {
                setTabSearchFields(prev => ({ ...prev, [typeKey]: [] }))
                loadedTabsRef.current.add(loadKey)
            } finally {
                setTabLoadingStates(prev => ({ ...prev, [typeKey]: false }))
            }
        })
        
        Promise.all(loadPromises).catch(() => {
            // Hatalar zaten individual catch'lerde handle edildi
        })
    }, [resolvedMultiTab, productTypes, resolvedLanguage, tabLoadingStates, tabSearchFields])
    
    // Language değiştiğinde cache'i temizle
    useEffect(() => {
        loadedTabsRef.current.clear()
    }, [resolvedLanguage])
    
    const activeSearchFields = useMemo(() => 
        resolvedMultiTab && productTypes.length > 0
            ? (tabSearchFields[productTypes[selectedTabIndex]?.type_key] || searchFields || [])
            : (searchFields || []),
        [resolvedMultiTab, productTypes, selectedTabIndex, tabSearchFields, searchFields]
    )
    
    const activeTabLoading = useMemo(() => 
        resolvedMultiTab && productTypes.length > 0
            ? (tabLoadingStates[productTypes[selectedTabIndex]?.type_key] || isLoading)
            : isLoading,
        [resolvedMultiTab, productTypes, selectedTabIndex, tabLoadingStates, isLoading]
    )
    
    const activeProductTypeForTab = useMemo(() => 
        resolvedMultiTab && productTypes.length > 0
            ? (productTypes[selectedTabIndex]?.type_key || resolvedActiveProductType)
            : resolvedActiveProductType,
        [resolvedMultiTab, productTypes, selectedTabIndex, resolvedActiveProductType]
    )
    
    const handleTabChange = useCallback((index: number) => {
        if (index < 0 || index >= productTypes.length) return
        setSelectedTabIndex(index)
    }, [productTypes.length])
    
    return {
        selectedTabIndex,
        tabSearchFields,
        tabLoadingStates,
        activeSearchFields,
        activeTabLoading,
        activeProductTypeForTab,
        setSelectedTabIndex,
        handleTabChange,
    }
}

